import { useTranslation } from '@klv/hooks/useTranslation';

import algoliaIndexes from '../data';

import { VALID_ACADEMY_RESOURCE_TYPES } from '../constants';

import type { TSearchIndexes } from '@klv/molecules/unified-search-results/types';
import type { IAcademyHit, ICommunityHit, IDeveloperPortalHit, IHelpCenterHit, IMarketingSiteHit } from '../types';

export default (index: TSearchIndexes) => {

  const { t } = useTranslation();

  let marketingSitePathPrefix = t('langCodes_pathPrefix');
  if (marketingSitePathPrefix) {
    marketingSitePathPrefix = '/' + marketingSitePathPrefix;
  }
  const { 
    academyIndex,
    communitIndex,
    developerIndex,
    helpCenterIndex,
    marketingSiteIndex,
  } = algoliaIndexes;

  const normalizeHits = {
    [academyIndex]: (hit: IAcademyHit) => {
      const academyHit = {
        title: hit?._highlightResult?.course_title?.value || hit?.course_title || '',
        courseId: hit?.objectID || '',
        divider: !!hit?.duration || (hit?.type === 'Live training' && hit?.next_events?.length > 0),
        duration: hit?.duration || '',
        description: hit?._highlightResult?.course_short_description?.value || hit?.course_short_description || '',
        link: hit.course_url,
        metadata: '', //TODO: add metadata when the CS team gets the academy index updated,
        nextEvents: hit?.next_events || [],
        pill: hit?.type || hit?.tags?.filter(tag => {
          if (VALID_ACADEMY_RESOURCE_TYPES.includes(tag)) {
            return tag;
          }
        }).join(', '),
      };
      return academyHit;
    },
    [communitIndex]: (hit: ICommunityHit) => { 
      const communityHit = {
        title: hit?._highlightResult?.title?.value || hit.title || '',
        divider: false,
        description: hit?.content || '',
        link: hit.url,
        metadata: !!hit?.answer_reply_id && t('pages_search_community_answered'),
      };
      return communityHit;
    },
    [developerIndex]: (hit: IDeveloperPortalHit) => {
      const communityHit = {
        title: hit?._highlightResult?.title?.value || hit.title || '',
        divider: false,
        description: hit?._snippetResult?.body?.value || hit?._snippetResult?.excerpt?.value || '',
        link: hit.link_url,
        metadata: '', //TODO: add "Minutes" metadata if/when the CS team gets the dev portal index updated.
      };
      return communityHit;
    },
    [helpCenterIndex]: (hit: IHelpCenterHit) => {
      const url = (hit.html_url ? hit.html_url : hit.url)?.replace('https://help.klaviyo.com', '')?.replace('https://klaviyo.zendesk.com', '');
      const idUrl = url.substring(0, url.split('-', 2).join('-').length);
      const helpCenterHit = {
        title: hit?._highlightResult?.title?.value || hit.title || '',
        divider: false,
        description: hit?._highlightResult?.preview_text?.value || '',
        body: hit?._snippetResult?.body?.value  || '',
        match: hit?._highlightResult?.preview_text?.matchLevel || '',
        link: `https://help.klaviyo.com${idUrl || url}`,
        metadata: '', //TODO: add metadata "minutes" if the data gets added to the help center index
      };
      return helpCenterHit;
    },
    [marketingSiteIndex]: (hit: IMarketingSiteHit) => {
      const marketingSiteHit = {
        title: hit._highlightResult?.title?.value || hit.title || '',
        divider: false,
        description: hit._highlightResult?.meta_description?.value || hit.meta_description || '', //TODO: add description when the CS team gets the marketing site index updated
        link: `http://www.klaviyo.com${marketingSitePathPrefix}${hit.uri}`,
        metadata: '', //TODO: add metadata if and when meta data is determined.
      };
      return marketingSiteHit;
    },
  };

  return normalizeHits[index];
};
